import { useMutation } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { AreaWorkspace } from 'types/area-workspace';
import { VERDICT_TO_KIND } from 'types/well-workspace';
import {
  apiPath,
  readWriteFailure,
  toWorkspaceError,
  UNEXPLAINED_FAILURE,
} from './utils';

export interface WorkspacePromotion {
  /** True while the promotion is in flight. */
  busy: boolean;
  /** Why the last attempt was refused, or null. */
  error: string | null;
  /** Publishes this workspace, with the reason typed into the panel. */
  promote: (reason?: string) => void;
  reset: () => void;
}

// Promoting publishes the workspace's area as the current version and freezes the
// workspace. One-way: Area Builder has no un-promote, so there is no inverse here and
// the button that calls it is driven off `resources.promotions.auth`.
export const useWorkspacePromotion = (
  promotionsHref: string,
  onPromoted: () => void
): WorkspacePromotion => {
  const { mutate, isPending, error, reset } = useMutation<
    AreaWorkspace,
    Error,
    string | undefined
  >({
    mutationFn: async (reason) => {
      try {
        return await api
          .post(apiPath(promotionsHref), {
            // Recorded as a comment on the area, so it shows in the activity feed.
            json: reason ? { reason } : {},
          })
          .json<AreaWorkspace>();
      } catch (requestError) {
        throw await toWorkspaceError(requestError);
      }
    },
    // A promotion is not safe to repeat, and a refusal is an answer rather than a blip.
    retry: false,
    onSuccess: () => onPromoted(),
  });

  return {
    busy: isPending,
    error: readWriteFailure(error, UNEXPLAINED_FAILURE),
    promote: (reason) => mutate(reason),
    reset,
  };
};

export interface CreateAreaWorkspace {
  /** True while the create is in flight, to hold the modal's button. */
  saving: boolean;
  /** Why the last attempt was refused, for the modal to show. */
  error: string | null;
  /** Resolves to the created workspace, or null when it was refused. */
  create: (name: string) => Promise<AreaWorkspace | null>;
  reset: () => void;
}

// Opens a workspace on an area — `POST /area_geometries/:id/workspaces` — answering with
// the envelope the caller then opens in place. The draft is always created off the map,
// and promote publishes its Promote to map flag as it stands, so a workspace opened on a
// shared area starts with that flag on: editing a shared area then keeps it on the map,
// and unticking the flag is how a user takes it off.
export const useCreateAreaWorkspace = (
  areaId: string | number,
  areaShared = false
): CreateAreaWorkspace => {
  const { mutateAsync, isPending, error, reset } = useMutation<
    AreaWorkspace,
    Error,
    string
  >({
    mutationFn: async (name) => {
      let created: AreaWorkspace;
      try {
        created = await api
          .post(`area_geometries/${areaId}/workspaces`, {
            // Wrapped and snake_cased to match the controller's strong params.
            json: { workspace: { name } },
          })
          .json<AreaWorkspace>();
      } catch (requestError) {
        throw await toWorkspaceError(requestError);
      }
      const draftHref = created.detail?.area?.href;
      if (areaShared && draftHref) {
        // The workspace exists either way, so a refused flag write doesn't stop it
        // opening: the checkbox then reads unticked, which is what was saved.
        await api
          .patch(apiPath(draftHref), { json: { shared: true } })
          .catch(() => undefined);
      }
      return created;
    },
    retry: false,
  });

  const create = async (name: string) => {
    try {
      return await mutateAsync(name);
    } catch {
      // The reason is read off `error`; a rejection here only keeps the modal open.
      return null;
    }
  };

  return {
    saving: isPending,
    error: readWriteFailure(error, UNEXPLAINED_FAILURE),
    create,
    reset,
  };
};

/** A verdict to set on the area, or `unanswered` to clear it. */
export type AreaVerdictInput = 'correct' | 'incorrect' | 'unanswered';

export interface AreaVerdictSetter {
  /** True while the verdict is in flight. */
  saving: boolean;
  /** Why the last attempt was refused, or null. */
  error: string | null;
  set: (
    verdict: AreaVerdictInput,
    reason?: string,
    onSaved?: () => void
  ) => void;
  reset: () => void;
}

/**
 * Mark as Correct / Incorrect on the workspace's area, as Well Status does it: an event on
 * the area's freeze item through the shared verification_items endpoint. The endpoint
 * answers with the event rather than the workspace, so the caller re-reads the workspace
 * to pick the new verdict up.
 * @param {string | undefined} eventsHref The freeze item's events link, from `markCorrect`.
 * @param {() => void} onSaved Re-reads the workspace once a verdict lands.
 * @return {AreaVerdictSetter} The setter, its in-flight flag and its refusal.
 */
export const useAreaVerdict = (
  eventsHref: string | undefined,
  onSaved: () => void
): AreaVerdictSetter => {
  const { mutate, isPending, error, reset } = useMutation<
    void,
    Error,
    { verdict: AreaVerdictInput; reason: string }
  >({
    mutationFn: async ({ verdict, reason }) => {
      // Nowhere to post: fail rather than resolve, which would read as saved.
      if (!eventsHref) throw new Error(UNEXPLAINED_FAILURE);
      try {
        await api.post(apiPath(eventsHref), {
          json: { kind: VERDICT_TO_KIND[verdict], reason },
        });
      } catch (requestError) {
        throw await toWorkspaceError(requestError);
      }
    },
    // A verdict is not safe to repeat, and a refusal is an answer rather than a blip.
    retry: false,
    onSuccess: () => onSaved(),
  });

  return {
    saving: isPending,
    error: readWriteFailure(error, UNEXPLAINED_FAILURE),
    set: (verdict, reason = '', done) =>
      mutate({ verdict, reason }, { onSuccess: () => done?.() }),
    reset,
  };
};
